import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api, getApiErrorMessage } from "@/shared/api/axios";
import { Button } from "@/shared/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/shared/components/ui/Card";
import { Input } from "@/shared/components/ui/Input";
import { Modal } from "@/shared/components/ui/Modal";
import { Skeleton } from "@/shared/components/ui/Skeleton";
import { useAccountsCustomers, useAccountsVendors } from "../hooks/useParties";
import { useChartOfAccountsList } from "../hooks/useChartOfAccounts";
import { usePurchaseBills, useSalesInvoices } from "../hooks/useTransactions";
import { localToday } from '@/shared/utils/utils';

type NoteType = "credit" | "debit";
type Line = {
  description: string;
  quantity: number;
  rate: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
};
const emptyLine = (): Line => ({
  description: "",
  quantity: 1,
  rate: 0,
  cgstRate: 0,
  sgstRate: 0,
  igstRate: 0,
});
const selectClass =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm";

export function CreditNotesList({ type }: { type: NoteType }) {
  const qc = useQueryClient();
  const isCredit = type === "credit";
  const endpoint = isCredit
    ? "/api/accounts/credit-notes"
    : "/api/accounts/debit-notes";
  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["accounts", `${type}-notes`],
    queryFn: async () => (await api.get<any[]>(endpoint)).data,
  });
  const { data: customers = [] } = useAccountsCustomers();
  const { data: vendors = [] } = useAccountsVendors();
  const { data: invoices = [] } = useSalesInvoices();
  const { data: bills = [] } = usePurchaseBills();
  const { data: accounts = [] } = useChartOfAccountsList({ active: "true" });
  const [editor, setEditor] = useState<any | null>(null);
  const [settle, setSettle] = useState<{
    note: any;
    mode: "apply" | "refund";
  } | null>(null);
  const [form, setForm] = useState({
    partyId: "",
    sourceId: "",
    date: localToday(),
    reason: "",
    currency: "INR",
    exchangeRate: 1,
    lines: [emptyLine()],
  });
  const [settleForm, setSettleForm] = useState({
    documentId: "",
    accountId: "",
    amount: 0,
    date: localToday(),
    reference: "",
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["accounts"] });
  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        [isCredit ? "customerId" : "vendorId"]: form.partyId,
        [isCredit ? "invoiceId" : "billId"]: form.sourceId || null,
        date: form.date,
        reason: form.reason,
        currency: form.currency,
        exchangeRate: form.exchangeRate,
        lineItems: form.lines,
      };
      return editor
        ? api.put(`${endpoint}/${editor._id}`, payload)
        : api.post(endpoint, payload, {
            headers: { "X-Idempotency-Key": crypto.randomUUID() },
          });
    },
    onSuccess: () => {
      refresh();
      setEditor(null);
      toast.success(editor ? "Draft updated." : "Draft credit created.");
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Could not save note.")),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${endpoint}/${id}`),
    onSuccess: () => {
      refresh();
      toast.success("Draft deleted.");
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Could not delete draft.")),
  });
  const action = useMutation({
    mutationFn: ({
      id,
      action,
      payload = {},
    }: {
      id: string;
      action: string;
      payload?: any;
    }) =>
      api.post(`${endpoint}/${id}/${action}`, payload, {
        headers: { "X-Idempotency-Key": crypto.randomUUID() },
      }),
    onSuccess: () => {
      refresh();
      setSettle(null);
      toast.success("Credit updated.");
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Credit action failed.")),
  });
  const parties: any[] = isCredit ? customers : vendors;
  const documents: any[] = isCredit ? invoices : bills;
  const resetEditor = (note?: any) => {
    setEditor(note || {});
    setForm(
      note
        ? {
            partyId: String(
              note[isCredit ? "customerId" : "vendorId"]?._id ||
                note[isCredit ? "customerId" : "vendorId"],
            ),
            sourceId: String(
              note[isCredit ? "invoiceId" : "billId"]?._id ||
                note[isCredit ? "invoiceId" : "billId"] ||
                "",
            ),
            date: new Date(note.date).toISOString().slice(0, 10),
            reason: note.reason,
            currency: note.currency || "INR",
            exchangeRate: note.exchangeRate || 1,
            lines: note.lineItems.map((line: any) => ({
              description: line.description,
              quantity: line.quantity,
              rate: line.rate,
              cgstRate: line.cgstRate || 0,
              sgstRate: line.sgstRate || 0,
              igstRate: line.igstRate || 0,
            })),
          }
        : {
            partyId: "",
            sourceId: "",
            date: localToday(),
            reason: "",
            currency: "INR",
            exchangeRate: 1,
            lines: [emptyLine()],
          },
    );
  };
  const openSettlement = (note: any, mode: "apply" | "refund") => {
    setSettle({ note, mode });
    setSettleForm({
      documentId: "",
      accountId: "",
      amount: Number(note.remainingAmount || 0),
      date: localToday(),
      reference: "",
    });
  };
  const eligibleDocuments = settle
    ? documents.filter((document) => {
        const party = document[isCredit ? "customerId" : "vendorId"];
        return (
          String(party?._id || party) ===
            String(
              settle.note[isCredit ? "customerId" : "vendorId"]?._id ||
                settle.note[isCredit ? "customerId" : "vendorId"],
            ) &&
          ["unpaid", "partially_paid", "overdue"].includes(document.status) &&
          Number(document.amountDue) > 0 &&
          (!document.currency || document.currency === settle.note.currency)
        );
      })
    : [];
  const lineTotal = form.lines.reduce(
    (sum, line) =>
      sum +
      Number(line.quantity || 0) *
        Number(line.rate || 0) *
        (1 +
          (Number(line.cgstRate || 0) +
            Number(line.sgstRate || 0) +
            Number(line.igstRate || 0)) /
            100),
    0,
  );
  if (isLoading) return <Skeleton className="h-[300px]" />;
  return (
    <>
      <Card className="shadow-xs">
        <CardHeader
          title={isCredit ? "Credit Notes" : "Vendor Credits"}
          subtitle="Draft, post, partially apply, refund, or void credits with a complete settlement trail."
          action={
            <Button size="sm" onClick={() => resetEditor()}>
              + New Credit
            </Button>
          }
        />
        <CardBody className="p-0">
          <div className="overflow-auto max-h-[520px]">
            <table className="w-full min-w-[950px] text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr>
                  <th className="px-4 py-3">Note #</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">
                    {isCredit ? "Customer" : "Vendor"}
                  </th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">Available</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {notes.map((note: any) => (
                  <tr key={note._id} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium">{note.noteNumber}</td>
                    <td className="px-4 py-3">
                      {new Date(note.date).toLocaleDateString("en-IN")}
                    </td>
                    <td className="px-4 py-3">
                      {note[isCredit ? "customerId" : "vendorId"]?.name || "-"}
                    </td>
                    <td className="px-4 py-3 max-w-[180px] truncate">
                      {note.reason}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {formatMoney(note.grandTotal, note.currency)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {formatMoney(note.remainingAmount, note.currency)}
                    </td>
                    <td className="px-4 py-3 capitalize">
                      {String(note.status).replace("_", " ")}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {note.status === "draft" && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => resetEditor(note)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                action.mutate({ id: note._id, action: "post" })
                              }
                            >
                              Post
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                confirm("Delete this draft?") &&
                                remove.mutate(note._id)
                              }
                            >
                              Delete
                            </Button>
                          </>
                        )}
                        {["open", "partially_applied"].includes(
                          note.status,
                        ) && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openSettlement(note, "apply")}
                            >
                              Apply
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openSettlement(note, "refund")}
                            >
                              Refund
                            </Button>
                          </>
                        )}
                        {note.status === "open" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const reason = prompt("Void reason:");
                              if (reason)
                                action.mutate({
                                  id: note._id,
                                  action: "void",
                                  payload: {
                                    reason,
                                    date: localToday(),
                                  },
                                });
                            }}
                          >
                            Void
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!notes.length && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-muted-foreground"
                    >
                      No credits found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
      <Modal
        open={!!editor}
        onClose={() => setEditor(null)}
        title={`${editor?._id ? "Edit" : "New"} ${isCredit ? "Credit Note" : "Vendor Credit"}`}
      size="lg"
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-4 md:grid-cols-3">
            <Field label={isCredit ? "Customer" : "Vendor"}>
              <select
                required
                disabled={!!editor?._id}
                className={selectClass}
                value={form.partyId}
                onChange={(event) =>
                  setForm({
                    ...form,
                    partyId: event.target.value,
                    sourceId: "",
                  })
                }
              >
                <option value="">Select party</option>
                {parties.map((party) => (
                  <option key={party._id} value={party._id}>
                    {party.name || "Unnamed"}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={`Source ${isCredit ? "invoice" : "bill"} (optional)`}>
              <select
                disabled={!!editor?._id}
                className={selectClass}
                value={form.sourceId}
                onChange={(event) =>
                  setForm({ ...form, sourceId: event.target.value })
                }
              >
                <option value="">Independent credit</option>
                {documents
                  .filter(
                    (doc) =>
                      String(
                        doc[isCredit ? "customerId" : "vendorId"]?._id ||
                          doc[isCredit ? "customerId" : "vendorId"],
                      ) === form.partyId,
                  )
                  .map((doc) => (
                    <option key={doc._id} value={doc._id}>
                      {doc.invoiceNumber || doc.billNumber}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Date">
              <Input
                required
                type="date"
                value={form.date}
                onChange={(event) =>
                  setForm({ ...form, date: event.target.value })
                }
              />
            </Field>
            <Field label="Currency">
              <Input
                required
                maxLength={3}
                value={form.currency}
                onChange={(event) =>
                  setForm({
                    ...form,
                    currency: event.target.value.toUpperCase(),
                  })
                }
              />
            </Field>
            <Field label="Exchange rate">
              <Input
                required
                type="number"
                min="0.00000001"
                step="any"
                value={form.exchangeRate}
                onChange={(event) =>
                  setForm({ ...form, exchangeRate: Number(event.target.value) })
                }
              />
            </Field>
            <Field label="Reason">
              <Input
                required
                value={form.reason}
                onChange={(event) =>
                  setForm({ ...form, reason: event.target.value })
                }
              />
            </Field>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-[2fr_repeat(5,1fr)_auto] gap-2 text-xs font-medium text-muted-foreground">
              <span>Description</span>
              <span>Qty</span>
              <span>Rate</span>
              <span>CGST %</span>
              <span>SGST %</span>
              <span>IGST %</span>
              <span />
            </div>
            {form.lines.map((line, index) => (
              <div
                key={index}
                className="grid grid-cols-[2fr_repeat(5,1fr)_auto] gap-2"
              >
                <Input
                  required
                  value={line.description}
                  onChange={(event) =>
                    updateLine(
                      index,
                      "description",
                      event.target.value,
                      form,
                      setForm,
                    )
                  }
                />
                <Input
                  required
                  type="number"
                  min="0.000001"
                  step="any"
                  value={line.quantity}
                  onChange={(event) =>
                    updateLine(
                      index,
                      "quantity",
                      Number(event.target.value),
                      form,
                      setForm,
                    )
                  }
                />
                <Input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.rate}
                  onChange={(event) =>
                    updateLine(
                      index,
                      "rate",
                      Number(event.target.value),
                      form,
                      setForm,
                    )
                  }
                />
                {(["cgstRate", "sgstRate", "igstRate"] as const).map(
                  (field) => (
                    <Input
                      key={field}
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={line[field]}
                      onChange={(event) =>
                        updateLine(
                          index,
                          field,
                          Number(event.target.value),
                          form,
                          setForm,
                        )
                      }
                    />
                  ),
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={form.lines.length === 1}
                  onClick={() =>
                    setForm({
                      ...form,
                      lines: form.lines.filter((_, i) => i !== index),
                    })
                  }
                >
                  ×
                </Button>
              </div>
            ))}
            <div className="flex justify-between">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  setForm({ ...form, lines: [...form.lines, emptyLine()] })
                }
              >
                + Add line
              </Button>
              <span className="font-semibold">
                Estimated total: {formatMoney(lineTotal, form.currency)}
              </span>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditor(null)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving..." : "Save Draft"}
            </Button>
          </div>
        </form>
      </Modal>
      <Modal
        open={!!settle}
        onClose={() => setSettle(null)}
        title={`${settle?.mode === "apply" ? "Apply" : "Refund"} Credit`}
        size="md"
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!settle) return;
            action.mutate({
              id: settle.note._id,
              action: settle.mode,
              payload:
                settle.mode === "apply"
                  ? {
                      [isCredit ? "invoiceId" : "billId"]:
                        settleForm.documentId,
                      amount: settleForm.amount,
                    }
                  : {
                      accountId: settleForm.accountId,
                      amount: settleForm.amount,
                      date: settleForm.date,
                      reference: settleForm.reference,
                    },
            });
          }}
        >
          {settle?.mode === "apply" ? (
            <Field label={isCredit ? "Invoice" : "Bill"}>
              <select
                required
                className={selectClass}
                value={settleForm.documentId}
                onChange={(event) =>
                  setSettleForm({
                    ...settleForm,
                    documentId: event.target.value,
                  })
                }
              >
                <option value="">Select open document</option>
                {eligibleDocuments.map((doc) => (
                  <option key={doc._id} value={doc._id}>
                    {doc.invoiceNumber || doc.billNumber} — due{" "}
                    {formatMoney(doc.amountDue, doc.currency)}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <>
              <Field label="Bank / cash account">
                <select
                  required
                  className={selectClass}
                  value={settleForm.accountId}
                  onChange={(event) =>
                    setSettleForm({
                      ...settleForm,
                      accountId: event.target.value,
                    })
                  }
                >
                  <option value="">Select account</option>
                  {accounts
                    .filter(
                      (account: any) =>
                        account.type === "asset" &&
                        (account.code === "1010" ||
                          account.code === "1020" ||
                          /bank|cash/i.test(account.name)),
                    )
                    .map((account: any) => (
                      <option key={account._id} value={account._id}>
                        {account.code} — {account.name}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Refund date">
                <Input
                  required
                  type="date"
                  value={settleForm.date}
                  onChange={(event) =>
                    setSettleForm({ ...settleForm, date: event.target.value })
                  }
                />
              </Field>
              <Field label="Reference">
                <Input
                  value={settleForm.reference}
                  onChange={(event) =>
                    setSettleForm({
                      ...settleForm,
                      reference: event.target.value,
                    })
                  }
                />
              </Field>
            </>
          )}
          <Field
            label={`Amount (available ${formatMoney(settle?.note.remainingAmount, settle?.note.currency)})`}
          >
            <Input
              required
              type="number"
              min="0.01"
              max={settle?.note.remainingAmount}
              step="0.01"
              value={settleForm.amount}
              onChange={(event) =>
                setSettleForm({
                  ...settleForm,
                  amount: Number(event.target.value),
                })
              }
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSettle(null)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={action.isPending}>
              Confirm
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

function updateLine(
  index: number,
  field: keyof Line,
  value: string | number,
  form: any,
  setForm: (value: any) => void,
) {
  setForm({
    ...form,
    lines: form.lines.map((line: Line, i: number) =>
      i === index ? { ...line, [field]: value } : line,
    ),
  });
}
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2 text-sm">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
function formatMoney(value: unknown, currency = "INR") {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
    }).format(Number(value) || 0);
  } catch {
    return `${currency} ${(Number(value) || 0).toFixed(2)}`;
  }
}
